// src/pages/Settings/Dossier/SettingsDossierInscription.jsx
import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  getAllSegments,
  addSegment,
  updateSegment,
  deleteSegment,
} from "../../../services/segmentDossierServices";
import {
  addFichier,
  updateFichier,
  deleteFichier,
} from "../../../services/fichierDossierServices";
import { toast } from "react-toastify";
import {
  FaFolderOpen,
  FaPlus,
  FaSave,
  FaTrash,
  FaListOl,
  FaFileAlt,
  FaLink,
  FaEdit,
} from "react-icons/fa";

// Quill toolbar
const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    ["link", "image", "video"],
    ["clean"],
  ],
};

const formats = [
  "header","font","size",
  "bold","italic","underline","strike","blockquote",
  "color","background",
  "script",
  "list","bullet","indent",
  "link","image","video",
];

export default function SettingsDossierInscription() {
  const [loading, setLoading] = useState(true);
  const [segments, setSegments] = useState([]);
  const [fileForms, setFileForms] = useState({});
  const [fileToEditId, setFileToEditId] = useState({}); // par segment
  const [newSegment, setNewSegment] = useState({
    segment_name: "",
    segment_content: "",
    ordre_segment: 0,
  });
  const [newFormKey, setNewFormKey] = useState(0); // ✅ force le remount du Quill "Nouveau segment"

  useEffect(() => {
    fetchSegments();
  }, []);

  async function fetchSegments() {
    setLoading(true);
    try {
      const data = await getAllSegments();
      // On garde une copie locale éditable
      const cloned = Array.isArray(data) ? data.map((s) => ({ ...s })) : [];
      setSegments(cloned);

      // Formulaires fichiers vides par segment
      const initial = {};
      cloned.forEach((s) => {
        initial[s.id] = { nom_fichier: "", lien_fichier: "", detail_fichier: "" };
      });
      setFileForms(initial);
    } catch (e) {
      console.error(e);
      toast.error("Impossible de charger les segments.");
    } finally {
      setLoading(false);
    }
  }

  // ==== Nouveau segment ====
  const handleAddSegment = async () => {
    if (!newSegment.segment_name.trim()) {
      toast.error("Le nom du segment est requis.");
      return;
    }
    try {
      await addSegment(newSegment);
      toast.success("Segment ajouté.");
      setNewSegment({ segment_name: "", segment_content: "", ordre_segment: 0 });
      setNewFormKey((k) => k + 1); // ✅ corrige le Quill qui gardait l’ancien contenu
      fetchSegments();
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l’ajout du segment.");
    }
  };

  // ==== Édition segment (local) ====
  const handleLocalSegmentChange = (id, field, value) => {
    setSegments((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [field]: value } : s))
    );
  };

  const handleSaveSegment = async (id) => {
    const seg = segments.find((s) => s.id === id);
    if (!seg) return;
    try {
      await updateSegment(id, {
        segment_name: seg.segment_name,
        segment_content: seg.segment_content,
        ordre_segment: Number(seg.ordre_segment) || 0,
      });
      toast.success("Segment enregistré.");
      fetchSegments();
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l’enregistrement du segment.");
    }
  };

  const handleDeleteSegment = async (id) => {
    if (!window.confirm("Supprimer ce segment ?")) return;
    try {
      await deleteSegment(id);
      toast.success("Segment supprimé.");
      fetchSegments();
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la suppression du segment.");
    }
  };

  // ==== FICHIERS ====
  const handleFileChange = (segmentId, field, value) => {
    setFileForms((prev) => ({
      ...prev,
      [segmentId]: { ...prev[segmentId], [field]: value },
    }));
  };

  const handleEditFile = (file, segmentId) => {
    setFileForms((prev) => ({
      ...prev,
      [segmentId]: {
        id: file.id,
        nom_fichier: file.nom_fichier,
        lien_fichier: file.lien_fichier,
        detail_fichier: file.detail_fichier,
      },
    }));
    setFileToEditId((prev) => ({ ...prev, [segmentId]: file.id }));
  };

  const resetFileForm = (segmentId) => {
    setFileForms((prev) => ({
      ...prev,
      [segmentId]: { nom_fichier: "", lien_fichier: "", detail_fichier: "" },
    }));
    setFileToEditId((prev) => ({ ...prev, [segmentId]: null }));
  };

  const handleAddFile = async (segmentId) => {
    const f = fileForms[segmentId] || {};
    if (!f.nom_fichier?.trim() || !f.lien_fichier?.trim()) {
      toast.error("Nom et lien du fichier sont requis.");
      return;
    }
    try {
      await addFichier({ segment_id: segmentId, ...f });
      toast.success("Fichier ajouté.");
      resetFileForm(segmentId);
      fetchSegments();
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l’ajout du fichier.");
    }
  };

  const handleUpdateFile = async (segmentId) => {
    const f = fileForms[segmentId];
    if (!f?.id) return;
    try {
      await updateFichier(f.id, f);
      toast.success("Fichier modifié.");
      resetFileForm(segmentId);
      fetchSegments();
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la modification du fichier.");
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm("Supprimer ce fichier ?")) return;
    try {
      await deleteFichier(fileId);
      toast.success("Fichier supprimé.");
      fetchSegments();
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la suppression du fichier.");
    }
  };

  return (
    <section className="w-full">
      <div className="mx-auto max-w-6xl px-3 sm:px-6">
        {/* Header */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center text-white">
                <FaFolderOpen />
              </div>
              <div>
                <h1 className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
                  Dossier d’Inscription — Segments & Fichiers
                </h1>
                <p className="text-white/90 text-sm sm:text-[15px] mt-1">
                  Créez des sections structurées et associez des documents téléchargeables.
                </p>
              </div>
            </div>
          </div>

          {/* Contenu */}
          <div className="p-4 sm:p-6 space-y-8">
            {/* Nouveau segment */}
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/50 p-4 sm:p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  ➕ Nouveau segment
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[1fr,180px] gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    Nom du segment
                  </label>
                  <input
                    type="text"
                    value={newSegment.segment_name}
                    onChange={(e) =>
                      setNewSegment((p) => ({ ...p, segment_name: e.target.value }))
                    }
                    placeholder="Ex : Documents à fournir"
                    className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    Ordre d’affichage
                  </label>
                  <div className="mt-1 relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                      <FaListOl />
                    </span>
                    <input
                      type="number"
                      value={newSegment.ordre_segment}
                      onChange={(e) =>
                        setNewSegment((p) => ({
                          ...p,
                          ordre_segment: Number(e.target.value) || 0,
                        }))
                      }
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-3 rounded-xl overflow-hidden border border-slate-300 dark:border-slate-700">
                <ReactQuill
                  key={newFormKey} // ✅ force le reset visuel après ajout
                  theme="snow"
                  value={newSegment.segment_content}
                  onChange={(value) =>
                    setNewSegment((p) => ({ ...p, segment_content: value }))
                  }
                  placeholder="Contenu du segment"
                  modules={modules}
                  formats={formats}
                />
              </div>

              <div className="mt-4 flex items-center justify-end gap-3">
                <button
                  onClick={() => {
                    setNewSegment({ segment_name: "", segment_content: "", ordre_segment: 0 });
                    setNewFormKey((k) => k + 1);
                  }}
                  className="px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700"
                  type="button"
                >
                  Réinitialiser
                </button>
                <button
                  onClick={handleAddSegment}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-violet-600 hover:bg-violet-700 text-white shadow"
                  type="button"
                >
                  <FaPlus /> Ajouter ce segment
                </button>
              </div>
            </div>

            {/* Segments existants */}
            {loading && (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-28 rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse" />
                ))}
              </div>
            )}

            {!loading && segments.length === 0 && (
              <div className="text-center py-10">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h4 className="mt-4 text-slate-700 dark:text-slate-200 font-semibold">
                  Aucun segment pour le moment
                </h4>
                <p className="text-slate-500 dark:text-slate-400 text-sm">
                  Utilisez “Nouveau segment” pour commencer.
                </p>
              </div>
            )}

            {!loading &&
              segments.map((segment) => (
                <div
                  key={segment.id}
                  className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/50 p-4 sm:p-5"
                >
                  {/* Header segment */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="grid grid-cols-1 md:grid-cols-[1fr,180px] gap-3 w-full">
                      <div>
                        <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                          Nom du segment
                        </label>
                        <input
                          type="text"
                          value={segment.segment_name}
                          onChange={(e) =>
                            handleLocalSegmentChange(
                              segment.id,
                              "segment_name",
                              e.target.value
                            )
                          }
                          className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                          Ordre
                        </label>
                        <input
                          type="number"
                          value={segment.ordre_segment}
                          onChange={(e) =>
                            handleLocalSegmentChange(
                              segment.id,
                              "ordre_segment",
                              Number(e.target.value) || 0
                            )
                          }
                          className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleSaveSegment(segment.id)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white shadow"
                        type="button"
                        title="Enregistrer ce segment"
                      >
                        <FaSave /> Enregistrer
                      </button>
                      <button
                        onClick={() => handleDeleteSegment(segment.id)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white shadow"
                        type="button"
                        title="Supprimer ce segment"
                      >
                        <FaTrash /> Supprimer
                      </button>
                    </div>
                  </div>

                  {/* Contenu */}
                  <div className="mt-3 rounded-xl overflow-hidden border border-slate-300 dark:border-slate-700">
                    <ReactQuill
                      theme="snow"
                      value={segment.segment_content}
                      onChange={(value) =>
                        handleLocalSegmentChange(segment.id, "segment_content", value)
                      }
                      placeholder="Contenu du segment"
                      modules={modules}
                      formats={formats}
                    />
                  </div>

                  {/* FICHIERS */}
                  <div className="mt-6">
                    <h5 className="font-semibold text-slate-800 dark:text-slate-100 mb-2">
                      📂 Fichiers liés
                    </h5>

                    {/* Liste */}
                    <ul className="space-y-2 mb-3">
                      {segment.fichiers?.map((file) => (
                        <li
                          key={file.id}
                          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white/60 dark:bg-slate-900/40 px-3 py-2"
                        >
                          <div className="flex items-center gap-2">
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-100">
                              <FaFileAlt />
                            </span>
                            <div className="text-sm">
                              <a
                                href={file.lien_fichier}
                                className="text-violet-600 hover:underline flex items-center gap-1"
                                target="_blank"
                                rel="noreferrer"
                                title={file.lien_fichier}
                              >
                                <FaLink className="opacity-80" /> {file.nom_fichier}
                              </a>
                              {file.detail_fichier && (
                                <div className="text-slate-500 dark:text-slate-400 text-xs">
                                  {file.detail_fichier}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditFile(file, segment.id)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm"
                              type="button"
                              title="Modifier"
                            >
                              <FaEdit /> Modifier
                            </button>
                            <button
                              onClick={() => handleDeleteFile(file.id)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm"
                              type="button"
                              title="Supprimer"
                            >
                              <FaTrash /> Supprimer
                            </button>
                          </div>
                        </li>
                      ))}
                      {(!segment.fichiers || segment.fichiers.length === 0) && (
                        <li className="text-sm text-slate-500 dark:text-slate-400">
                          Aucun fichier pour ce segment.
                        </li>
                      )}
                    </ul>

                    {/* Formulaire fichier */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                          Nom du fichier
                        </label>
                        <input
                          type="text"
                          className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-sm"
                          placeholder="Ex : Autorisation parentale"
                          value={fileForms[segment.id]?.nom_fichier || ""}
                          onChange={(e) =>
                            handleFileChange(segment.id, "nom_fichier", e.target.value)
                          }
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                          Lien du fichier
                        </label>
                        <input
                          type="text"
                          className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-sm"
                          placeholder="URL https://…"
                          value={fileForms[segment.id]?.lien_fichier || ""}
                          onChange={(e) =>
                            handleFileChange(segment.id, "lien_fichier", e.target.value)
                          }
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                          Détail (optionnel)
                        </label>
                        <input
                          type="text"
                          className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-sm"
                          placeholder="PDF, à imprimer et signer"
                          value={fileForms[segment.id]?.detail_fichier || ""}
                          onChange={(e) =>
                            handleFileChange(segment.id, "detail_fichier", e.target.value)
                          }
                        />
                      </div>
                    </div>

                    <div className="mt-3 flex items-center justify-end gap-2">
                      {fileToEditId[segment.id] ? (
                        <>
                          <button
                            onClick={() => resetFileForm(segment.id)}
                            className="px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm"
                            type="button"
                          >
                            Annuler
                          </button>
                          <button
                            onClick={() => handleUpdateFile(segment.id)}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm"
                            type="button"
                          >
                            <FaSave /> Modifier ce fichier
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleAddFile(segment.id)}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm"
                          type="button"
                        >
                          <FaPlus /> Ajouter ce fichier
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </section>
  );
}
